import React from 'react';
import styles from './SeasonSparkline.module.css';

const SeasonSparkline = ({ data = [] }) => {
    // If data is empty or null, provide a safe fallback or return null
    const safeData = (data && data.length > 0) 
        ? data.map(v => parseFloat(v) || 0) 
        : [1, 1, 1, 1, 1, 1, 1, 1, 1, 1];

    const min = 0;
    const max = 10;
    const width = 665;
    const height = 480;
    const paddingLeft = 60;
    const paddingRight = 60;
    const paddingTop = 30;
    const paddingBottom = 35; // Reduced from 50

    const getX = (i) => paddingLeft + (i * (width - paddingLeft - paddingRight)) / (Math.max(1, safeData.length - 1));
    const getY = (val) => height - paddingBottom - ((val - min) * (height - paddingTop - paddingBottom)) / (max - min);

    // Calculate Summary Stats
    const high = Math.max(...safeData).toFixed(1);
    const low = Math.min(...safeData).toFixed(1);
    const avg = (safeData.reduce((a, b) => a + b, 0) / safeData.length).toFixed(1);
    const aboveAvg = safeData.filter(v => v >= parseFloat(avg)).length;
    const latestRating = safeData[safeData.length - 1] || 0;
    const trending = (latestRating - (safeData[safeData.length - 2] || 0)).toFixed(1);
    const isTrendingUp = parseFloat(trending) >= 0;

    // Match numbers (10 to 1, where 1 is latest)
    const matchLabels = Array.from({ length: safeData.length }, (_, i) => safeData.length - i);

    // Smooth Path
    let d = `M ${getX(0)} ${getY(safeData[0])}`;
    for (let i = 0; i < safeData.length - 1; i++) {
        const x1 = getX(i);
        const y1 = getY(safeData[i]);
        const x2 = getX(i + 1);
        const y2 = getY(safeData[i + 1]);
        const cx = (x1 + x2) / 2;
        d += ` C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`;
    }

    return (
        <div className={styles['momentum-container']}>
            {/* Header Section */}
            <div className={styles['sparkline-header']}>
                <div className={styles['sparkline-title-area']}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '0.4rem' }}>
                        <div className={styles['momentum-dot']}></div>
                        <h3 className={styles['momentum-title']}>Season Momentum</h3>
                    </div>
                </div>
                <div className={styles['current-form-box']}>
                    <span style={{ color: 'var(--primary-color)', fontSize: '2.2rem', fontWeight: '900', display: 'block', lineHeight: 1 }}>{latestRating.toFixed(1)}</span>
                    <span style={{ color: '#666', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '900', letterSpacing: '1px' }}>Current Form</span>
                </div>
            </div>

            {/* Summary Metrics Row */}
            <div className={styles['summary-metrics-row']}>
                <div className={styles['summary-item']}>
                    <span className={styles['summary-value']}>{high}</span>
                    <span className={styles['summary-label']}>Season High</span>
                </div>
                <div className={styles['summary-item']}>
                    <span className={styles['summary-value']}>{low}</span>
                    <span className={styles['summary-label']}>Season Low</span>
                </div>
                <div className={styles['summary-item']}>
                    <span className={styles['summary-value']}>{avg}</span>
                    <span className={styles['summary-label']}>Average</span>
                </div>
                <div className={styles['summary-item']}>
                    <span className={styles['summary-value']}>{aboveAvg}/{safeData.length}</span>
                    <span className={styles['summary-label']}>Above Avg</span>
                </div>
                <div className={styles['summary-item']}>
                    <span className={styles['summary-value']}>{safeData.length}</span>
                    <span className={styles['summary-label']}>Matches tracked</span>
                </div>
            </div>

            {/* SVG Graph Area */}
            <div style={{ position: 'relative' }}>
                <svg viewBox={`0 0 ${width} ${height}`} className={styles['sparkline-svg-container']} width="100%">
                    <defs>
                        <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="var(--primary-color)" stopOpacity="0.15" />
                            <stop offset="100%" stopColor="var(--primary-color)" stopOpacity="0" />
                        </linearGradient>
                        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                            <feGaussianBlur stdDeviation="3" result="blur" />
                            <feComposite in="SourceGraphic" in2="blur" operator="over" />
                        </filter>
                    </defs>

                    {/* Y-Axis Labels */}
                    {[0, 2.5, 5, 7.5, 10].map(val => (
                        <g key={val}>
                            <text x={paddingLeft - 15} y={getY(val) + 3} className={styles['svg-text-y']} textAnchor="end">{val}</text>
                            <line x1={paddingLeft} y1={getY(val)} x2={width - paddingRight} y2={getY(val)} className={styles['svg-line-grid']} />
                        </g>
                    ))}

                    <path d={d} fill="none" stroke="var(--primary-color)" strokeWidth="2" strokeLinecap="round" filter="url(#glow)" />

                    {/* Data Points */}
                    {safeData.map((val, i) => {
                        const isLast = i === safeData.length - 1;
                        return (
                            <g key={i}>
                                {/* X-Axis Match Label */}
                                <text x={getX(i)} y={height - 8} className={styles['svg-text-x']} textAnchor="middle">{matchLabels[i]}</text>

                                {/* Point Circle */}
                                <circle cx={getX(i)} cy={getY(val)} r="4" fill={isLast ? "var(--primary-color)" : "#0a0a0a"} stroke="var(--primary-color)" strokeWidth="1.5" />
                                {isLast && <circle cx={getX(i)} cy={getY(val)} r="7" stroke="#fff" strokeWidth="2" fill="none" />}
                            </g>
                        );
                    })}

                    {/* Left side label */}
                    <text 
                        x={20} 
                        y={height / 2} 
                        transform={`rotate(-90, 20, ${height / 2})`}
                        fill="#888"
                        fontSize="14"
                        fontWeight="900"
                        textAnchor="middle"
                        letterSpacing="1.5px"
                    >
                        PLAYER RATING
                    </text>
                </svg>
                
                {/* X-axis title centered below */}
                <div style={{ 
                    textAlign: 'center', 
                    color: '#888', 
                    fontSize: '0.875rem', 
                    fontWeight: '800', 
                    textTransform: 'uppercase', 
                    marginTop: '0px', 
                    letterSpacing: '2px' 
                }}>
                    Last {safeData.length} matches
                </div>
            </div>
        </div>
    );
};

export default SeasonSparkline;
